/**
 * The front door and everything behind it that is still not the application: the sign-in
 * options, the local account's two steps, enrolment, and the pages a sign-in can end on.
 */
export const session = {
  signIn: {
    title: "Inloggen",
    description:
      "Log in om de ploegen van je korps in te schrijven of je beschikbaarheid door te geven.",
    withProvider: "Inloggen met {{provider}}",
    sso: {
      title: "Met het account van je korps of veiligheidsregio",
      hint: "Gebruik het account waarmee je ook bij je korps of regio inlogt.",
    },
    fallback: "Lukt dat niet?",
    inloglink: "Met een link per mail",
    local: "Inloggen met je e-mailadres en wachtwoord",
    noneConfigured: "Er is nog geen inlogmethode ingesteld voor deze omgeving.",
    // JBNL's own wording, not the protocol's. Nobody signs in with "an OIDC scheme".
    providers: {
      microsoft: "Microsoft",
      local: "e-mailadres en wachtwoord",
      inloglink: "een inloglink",
    },
  },

  local: {
    email: "E-mailadres",
    password: "Wachtwoord", // skipcq: SCT-A000 - a field label, not a credential
    submit: "Inloggen",
    code: {
      label: "Code uit je authenticator-app",
      recoveryHint: "Authenticator-app kwijt? Vul hier een van je herstelcodes in.",
    },
    noAccount: {
      // Split around the two links the sentence carries: the anchors have classes and a `target`.
      before: "Nog geen account? Zorg dat je in het ",
      portaal: "JBNL Portaal",
      between: " staat. Of stuur een mail naar ",
      after: ".",
    },
    // Every failure that could reveal whether an address holds an account shares one wording,
    // because the API deliberately answers them identically.
    errors: {
      wrongCode: "De code klopt niet. Probeer het opnieuw.",
      blocked:
        "Je hebt het te vaak geprobeerd. Probeer het over een kwartier opnieuw.",
      twoFactorBlocked:
        "Je account is geblokkeerd na te veel verkeerde codes. Neem contact op met ICT via {{mailbox}}.",
      failed: "Het e-mailadres of het wachtwoord klopt niet.",
      signInFailed: "Inloggen is niet gelukt. Probeer het opnieuw.",
      providersUnavailable:
        "We konden de inlogopties niet ophalen ({{status}}). Laad de pagina opnieuw.",
    },
  },

  /** Never the words "magic link": a volunteer reads one sentence about this, in Dutch. */
  inloglink: {
    what: "We mailen je een link waarmee je direct inlogt, zonder wachtwoord. De link werkt één keer en is {{minuten}} minuten geldig.",
    submit: "Inloglink sturen",

    sent: {
      title: "Kijk in je mailbox.",
      /** Names the address back without saying whether we know it: that would be a membership oracle. */
      body: "Is {{adres}} bij JBNL bekend? Dan krijg je binnen een paar minuten een inloglink. Gebruik het adres dat JBNL van je heeft. Dat is het adres van je veiligheidsregio, of het adres dat de wedstrijdleiders van je hebben.",
      again:
        "We hebben dezelfde link nog een keer gestuurd. De link blijft {{minuten}} minuten geldig na je eerste aanvraag.",
      noMail: "Geen mail? Controleer of het adres klopt, of mail {{mailbox}}.",
      resend: "Link opnieuw sturen",
    },

    tooOften: {
      title: "Je hebt vandaag vijf keer een inloglink aangevraagd.",
      body: "Probeer het morgen opnieuw, of mail {{mailbox}}. Dan sturen wij je zelf een inloglink.",
    },

    /** A fact about the system rather than about the address, so this one is honest. */
    unavailable: {
      title: "We kunnen nu geen inloglinks versturen.",
      body: "Probeer het later opnieuw. Je kunt ook inloggen met Microsoft of met je wachtwoord. Lukt dat niet? Mail dan {{mailbox}}.",
    },

    landing: {
      busy: "Even geduld, we loggen je in…",
      invalid: {
        title: "Deze inloglink werkt niet meer",
        body: "Een inloglink werkt maar één keer. Vraag op de aanmeldsite een nieuwe link aan met hetzelfde e-mailadres.",
      },
      unknown: {
        title: "We herkennen dit account nog niet",
        body: "De inloglink is geldig, maar we weten nog niet bij wie dit e-mailadres hoort. Mail {{mailbox}} met je naam en je korps.",
      },
      back: "Terug naar de aanmeldsite",
    },

    errors: {
      failed: "We konden de inloglink niet versturen. Probeer het opnieuw.",
    },
  },

  /** A developer tool on the sign-in page, labelled as one so it is never mistaken for a door. */
  dev: {
    badge: "debug",
    title: "Testaccounts",
    loading: "Testaccounts ophalen…",
    empty: "De seeder heeft nog geen testaccounts geschreven.",
    signInAs: "Inloggen als {{persoon}}",
    /** An ellipsis, not "Bezig…": the button is a cell code wide. */
    busy: "…",
    /** The persoon is not in the database - the seeder is older than the matrix. */
    missing: "ontbreekt",
    drift:
      "Wijkt af van de matrix. Herstart de API: de seeder zet de rechten terug.",

    columns: {
      code: "Cel",
      persoon: "Persoon",
      korpsen: "Korpsen",
      ondersteuner: "Ondersteuner",
      ondersteunerShort: "Ond.",
      beheerder: "Beheerder",
      beheerderShort: "Beh.",
    },

    korpsAccess: {
      None: "geen",
      OneKorps: "één korps",
      TwoKorpsen: "twee korpsen",
      ViaVeiligheidsregio: "via veiligheidsregio",
    },

    errors: {
      accounts: "Kon de testaccounts niet ophalen ({{status}}).",
      signIn: "Inloggen is mislukt ({{status}}).",
    },
  },
} as const;
